import { useFocusRing } from '@react-native-aria/focus'
import clsx from 'clsx'
import type { ReactNode } from 'react'
import { Pressable, View } from 'react-native'

import { Icon } from '../media/Icon'
import { Typography } from '../text/Typography'
import { chipVariants } from './chip-variants'
import type { TChipProps } from './chip.types'

export const Chip = ({
  iconName,
  testID,
  badgeInfo,
  text,
  className,
  classNames,
  onPress,
  colors = 'default',
  variant = 'solid',
  size = 'md',
  fontWeight = 'bold',
  style,
  dataSet,
}: TChipProps): ReactNode => {
  const { isFocusVisible, focusProps } = useFocusRing()

  const hasIcon = iconName != null
  const hasBadge = badgeInfo != null
  const hasText = !!text

  const isPressable = !!onPress

  const slots = chipVariants({
    colors,
    variant,
    size,
    hasIcon,
    hasBadge,
    hasText,
    isPressable,
  })

  const content = (
    <>
      {hasIcon && <Icon name={iconName} size="sm" className={slots.textColor({ className: classNames?.textColor })} />}
      {hasText && (
        <Typography
          numberOfLines={1}
          size={size === 'sm' ? 'xs' : 'sm'}
          bold={fontWeight === 'bold'}
          className={clsx(slots.text(), slots.textColor({ className: classNames?.textColor }))}
          dataSet={dataSet}
        >
          {text}
        </Typography>
      )}
      {hasBadge && (
        <View className={slots.badgeBackground({ className: classNames?.badgeBackground })}>
          <Typography
            size={size === 'sm' ? 'xs' : 'sm'}
            bold
            className={slots.badgeText({ className: classNames?.badgeText })}
          >
            {badgeInfo}
          </Typography>
        </View>
      )}
    </>
  )

  const sharedProps = {
    testID,
    className: slots.background({ className: clsx(className, classNames?.background) }),
    style: {
      maxHeight: 26, // only useful for mobile so flex doesn't take all the space
      ...style,
    },
  }

  if (!isPressable) {
    return <View {...sharedProps}>{content}</View>
  }

  return (
    <Pressable
      {...sharedProps}
      onPress={onPress}
      tabIndex={0}
      dataSet={{ 'focus-visible': isFocusVisible }}
      {...focusProps}
    >
      {content}
    </Pressable>
  )
}
